/**
 * File Summary Component
 * Displays a list of changed files with clickable links (prototype-aligned)
 */

import { FileText } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { testIds } from '@/lib/test-ids'
import { documentUrlForPathOrId } from '@/lib/app-routes'

interface FileSummaryProps {
  files: Array<{
    path: string
    name: string
    linesAdded: number
    linesRemoved?: number
  }>
  /**
   * Preferred open path - opens a document tab via the layout handler.
   * Bare `navigate()` only changes the address bar and often leaves the Explorer
   * tab active (URL updates, view does not).
   */
  onOpenFile?: (filePath: string, event?: React.MouseEvent) => void
}

export function FileSummary({ files, onOpenFile }: FileSummaryProps) {
  const navigate = useNavigate()

  const handleFileClick = (filePath: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (onOpenFile) {
      onOpenFile(filePath, e)
      return
    }
    // Fallback for isolated prototypes without a tab host
    navigate(documentUrlForPathOrId(filePath))
  }

  if (!files || files.length === 0) {
    return null
  }

  return (
    <div
      className="border border-border rounded-lg overflow-hidden bg-card slide-in-from-bottom-2 mt-4"
      data-testid={testIds.chatPane.fileSummaryCard}
    >
      {/* Header */}
      <div className="bg-muted/50 px-3 py-2 border-b border-border flex items-center justify-between">
        <span className="text-sm font-medium">{files.length} {files.length === 1 ? 'File' : 'Files'}</span>
      </div>

      {/* File list */}
      <div className="divide-y divide-border">
        {files.map((file, idx) => (
          <div
            key={idx}
            role="button"
            tabIndex={0}
            className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 transition-colors cursor-pointer group"
            onClick={(e) => handleFileClick(file.path, e)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleFileClick(file.path, e as unknown as React.MouseEvent)
              }
            }}
            title={file.path}
            data-testid={testIds.chatPane.fileSummaryRow(idx)}
          >
            {/* File icon */}
            <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />

            {/* File name (truncated) */}
            <span className="text-xs flex-1 truncate">
              {file.name}
            </span>

            {/* Line changes */}
            <div className="flex items-center gap-2 text-xs">
              {file.linesAdded > 0 && (
                <span className="text-green-500">+{file.linesAdded}</span>
              )}
              {file.linesRemoved && file.linesRemoved > 0 && (
                <span className="text-red-500">-{file.linesRemoved}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
